/**
 * Absolutely-positioned event block in the week/day views that the user
 * can grab and drop into a different time slot. Rendered *above* any
 * TimeSlotDropZones so pointer events hit the event first; when drag
 * starts, dnd-kit hands pointer capture to the drop zones underneath.
 *
 * Only schedule events with a numeric `item_id` (i.e. stored in MongoDB)
 * are draggable. Google Calendar events, virtual recurring occurrences,
 * and recurring masters are rendered as non-draggable so the API doesn't
 * get a PUT it can't satisfy.
 *
 * The optional X button (top-right) calls `onUnschedule` and is shown:
 *   - always while `Date.now() - createdAtMs < graceMs` (just-placed),
 *   - on hover/focus thereafter.
 * Pointer events on the X are stopped from bubbling so they don't start a
 * drag.
 */

import React, { useEffect, useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { format } from 'date-fns'
import { X } from 'lucide-react'

import type { CommitmentState } from '../../services/scheduleService'
import { CommitmentGlyph } from './CommitmentBadge'
import {
  COMMITMENT_HINT,
  COMMITMENT_LABEL,
  commitmentBorder,
  commitmentFillAlpha,
} from './commitmentPresentation'

export const EVENT_DRAG_TYPE = 'existing_event'

/**
 * Format a [start, end] window for a pill. Uses 24h HH:MM so both the
 * start and end stay compact on a single line (en-dash, no spaces) --
 * "06:00–06:05" fits in ~60px at 10px font, which is the tightest we
 * render at minHeight-clamped short pills. If `end` is missing we fall
 * back to just the start time.
 */
function formatTimeRange(start: Date, end?: Date): string {
  const startStr = format(start, 'HH:mm')
  if (!end) return startStr
  return `${startStr}–${format(end, 'HH:mm')}`
}

/**
 * Compact duration label: "5m", "45m", "1h", "1h 30m". Zero-minute
 * inputs collapse to an empty string so the caller's separator is
 * dropped entirely; anything <=60 minutes formats as plain minutes so
 * we don't force "0h 45m" noise.
 */
function formatDuration(minutes: number): string {
  if (!minutes || minutes <= 0) return ''
  if (minutes < 60) return `${minutes}m`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

export interface ExistingEventPayload {
  id: string
  itemId: number
  title: string
  description?: string
  start: Date
  end?: Date
  color: string
  /** minutes — preserved across drag so the move doesn't resize the block */
  durationMin: number
  // Recurrence context threaded through drag + click handlers so the
  // edit modal can offer "this occurrence / all occurrences" when
  // appropriate. All optional; missing values collapse to non-recurring
  // behavior, which is always safe.
  isRecurringInstance?: boolean
  isRecurringMaster?: boolean
  /** id of the recurring master (when this is an instance/exception) */
  masterScheduleId?: number
  /** habit_definitions.id when task_kind === 'habit_occurrence' */
  recurringSourceId?: number | string
  /** "habit_occurrence" | "task" | undefined */
  taskKind?: string
  /** YYYY-MM-DD. The date the occurrence was originally scheduled for. */
  originalDate?: string
  /**
   * §2 dim-4 calendar commitment, straight from the API. `null`/undefined
   * means the payload didn't say — the block then renders exactly as it
   * always has, with no lock affordance either way.
   */
  commitment?: CommitmentState | null
}

export interface ExistingEventDragData {
  kind: typeof EVENT_DRAG_TYPE
  event: ExistingEventPayload
}

interface DraggableEventBlockProps {
  event: ExistingEventPayload
  /** pixel box (already computed from time + hour-height) */
  top: number
  height: number
  /** set to false for events we explicitly want non-draggable */
  draggable?: boolean
  /** Mute styling when this event is in the past. */
  isPast?: boolean
  /** Click handler for the X — when omitted, the X is not rendered. */
  onUnschedule?: (itemId: number) => void
  /**
   * Click handler for the pill surface. Opens the event edit modal in
   * the parent. Separate from the drag listener because dnd-kit's
   * PointerSensor requires 6px of pointer movement before starting a
   * drag, so a stationary click lands here, and a drag never fires
   * this. Receives the React event `id` (always defined, even for
   * virtual recurring instances whose `itemId` is NaN). Leave
   * undefined to keep the legacy read-only pill.
   */
  onOpen?: (eventId: string) => void
  /** Epoch ms when this event was created locally. Drives the grace window. */
  createdAtMs?: number
  /** Grace window in ms during which the X is always visible. */
  graceMs?: number
  /**
   * Lane assignment for side-by-side layout when multiple events overlap
   * the same time window. Both optional: when omitted (or when
   * `laneCount <= 1`), the block renders full-width as before, preserving
   * behavior for callers that do not yet compute a lane layout.
   */
  laneIndex?: number
  laneCount?: number
}

export const DraggableEventBlock: React.FC<DraggableEventBlockProps> = ({
  event,
  top,
  height,
  draggable = true,
  isPast = false,
  onUnschedule,
  onOpen,
  createdAtMs,
  graceMs = 12_000,
  laneIndex = 0,
  laneCount = 1,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${EVENT_DRAG_TYPE}:${event.id}`,
    disabled: !draggable,
    data: { kind: EVENT_DRAG_TYPE, event } satisfies ExistingEventDragData,
  })

  const showX = !!onUnschedule
  // Re-render once the grace window expires so the always-visible X turns
  // into a hover-only X without waiting for any other prop to change.
  const [graceExpired, setGraceExpired] = useState<boolean>(() => {
    if (!createdAtMs) return true
    return Date.now() - createdAtMs >= graceMs
  })
  useEffect(() => {
    if (!createdAtMs || graceExpired) return
    const elapsed = Date.now() - createdAtMs
    const remaining = Math.max(graceMs - elapsed, 0)
    const id = window.setTimeout(() => setGraceExpired(true), remaining + 50)
    return () => window.clearTimeout(id)
  }, [createdAtMs, graceMs, graceExpired])
  const inGrace = !graceExpired

  // Commitment is additive to every label: a block that says nothing about
  // it reads exactly as it did before this surface existed.
  const commitmentTitle = event.commitment ? ` · ${COMMITMENT_HINT[event.commitment]}` : ''
  const commitmentSuffix = event.commitment
    ? `, ${COMMITMENT_LABEL[event.commitment]}`
    : ''

  // When `laneCount > 1` the caller has asked us to share this time slot
  // with sibling events. Lay out in percentage columns with a 2px gutter
  // so each pill reads as distinct. Single-lane keeps the legacy
  // full-width absolute layout (`left: 2px / right: 2px`).
  const multiLane = laneCount > 1
  const laneWidthPct = 100 / laneCount
  const laneLeftPct = laneIndex * laneWidthPct
  const style: React.CSSProperties = {
    position: 'absolute',
    top: `${top}px`,
    height: `${height}px`,
    ...(multiLane
      ? {
          left: `calc(${laneLeftPct}% + 2px)`,
          width: `calc(${laneWidthPct}% - 4px)`,
        }
      : {
          left: '2px',
          right: '2px',
        }),
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : isPast ? 0.6 : 1,
    backgroundColor: event.color + commitmentFillAlpha(event.commitment),
    color: event.color,
    borderLeft: `3px ${commitmentBorder(event.commitment)} ${event.color}`,
    cursor: draggable ? 'grab' : 'default',
    touchAction: draggable ? 'none' : undefined,
    filter: isPast ? 'saturate(0.75)' : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(draggable ? listeners : {})}
      {...(draggable ? attributes : {})}
      role={onOpen ? 'button' : draggable ? 'button' : undefined}
      // Stationary clicks (no drag) pass through dnd-kit's 6px activation
      // threshold and land here. We intentionally do NOT stopPropagation
      // before checking it was a real click because the drag listeners
      // above already bailed out if a drag started.
      onClick={(e) => {
        if (!onOpen) return
        // Ignore clicks that came from the unschedule X (already
        // stopPropagation'd), and clicks that look like the end of a
        // drag (detail === 0 is rare; we rely on PointerSensor's
        // activation distance instead).
        e.stopPropagation()
        onOpen(event.id)
      }}
      onKeyDown={(e) => {
        if (!onOpen) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(event.id)
        }
      }}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={
        onOpen
          ? `Edit ${event.title}${commitmentSuffix}${isPast ? ' (currently in the past)' : ''}`
          : draggable
          ? `Drag ${event.title} to a new time${commitmentSuffix}${isPast ? ' (currently in the past)' : ''}`
          : undefined
      }
      className={`group relative rounded px-1.5 py-0.5 text-app-micro leading-tight overflow-hidden shadow-sm select-none ${
        onOpen ? 'hover:ring-1 hover:ring-inset hover:ring-white/40 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white/60' : ''
      }`}
      title={`${event.title} · ${formatTimeRange(event.start, event.end)} · ${formatDuration(event.durationMin)}${commitmentTitle}${onOpen ? ' · click to edit' : ''}`}
    >
      <div className="flex items-center gap-1 pr-4">
        <CommitmentGlyph state={event.commitment} className="w-2.5 h-2.5" />
        <span className="font-medium truncate">{event.title}</span>
      </div>
      {/* Compact one-liner: "6:00–6:05 · 5m". Clipped by overflow-hidden
          on very short pills, where the title alone conveys the item.
          Users can still hover/tap to see the title tooltip for full
          context. */}
      <div className="text-app-nano opacity-80 truncate tabular-nums">
        {formatTimeRange(event.start, event.end)}
        {event.durationMin ? ` · ${formatDuration(event.durationMin)}` : ''}
      </div>
      {showX && (
        <button
          type="button"
          tabIndex={0}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onUnschedule?.(event.itemId)
          }}
          className={`absolute top-0.5 right-0.5 p-0.5 rounded-full bg-surface/80 text-bad hover:bg-bad/20 transition-opacity ${
            inGrace ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'
          }`}
          aria-label={`Unschedule ${event.title}`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  )
}

export default DraggableEventBlock
